import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { NgClass } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatTableModule, MatTableDataSource } from '@angular/material/table'
import { MatMenuModule } from '@angular/material/menu'
import { MatSortModule, Sort } from '@angular/material/sort'

import { TelefonePipe } from '../../pipes/phone.pipes'
import { FarolFasesComponent } from '../farol-fases/farol-fases.component'
import {
  AlteracaoVm,
  FaseFarolVm,
  LinhaMonitoramentoVm,
  normalizarCampoAlteracao,
  STATUS_OS_AGUARDANDO_CONFIRMACAO_OPERADOR,
  STATUS_OS_CANCELADO,
  STATUS_OS_CANCELADO_CONTRATO,
} from '../../../core/models/monitoring-os.model'

export interface ConfirmarFaseEvent {
  item: LinhaMonitoramentoVm
  fase: FaseFarolVm
}

// Tabela da Torre — tem exatamente este consumidor (pages/monitoring-os), por isso
// as colunas são fixas em vez de vir de um [columns] genérico: a linha agora tem
// dados aninhados (origem/destino/motorista/farol) demais pra caber no padrão
// keyColumn/key das outras table-list-*.
@Component({
  selector: 'table-list-monitoring-os',
  standalone: true,
  imports: [
    NgClass,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatMenuModule,
    MatSortModule,
    TelefonePipe,
    FarolFasesComponent,
  ],
  templateUrl: './table-list-monitoring-os.component.html',
  styleUrl: './table-list-monitoring-os.component.scss',
})
export class TableListMonitoringOsComponent implements OnChanges {
  @Input() dataSource: LinhaMonitoramentoVm[] = []
  @Input({ required: true }) agora!: Date

  @Output() editarOs = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() verPassageiros = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() mensagemMotorista = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() alterarMotorista = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() alterarVeiculo = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() ocorrencias = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() progresso = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() confirmarFase = new EventEmitter<ConfirmarFaseEvent>()
  @Output() marcarNoShow = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() desmarcarNoShow = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() confirmarEnvio = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() desfazerEnvio = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() confirmarAtribuicao = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() recusarAtribuicao = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() historico = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() cancelarOs = new EventEmitter<LinhaMonitoramentoVm>()
  @Output() sortChange = new EventEmitter<{ ordenarPor: string; direcao: 'asc' | 'desc' } | null>()

  readonly STATUS_OS_AGUARDANDO_CONFIRMACAO_OPERADOR = STATUS_OS_AGUARDANDO_CONFIRMACAO_OPERADOR
  readonly STATUS_OS_CANCELADO = STATUS_OS_CANCELADO
  readonly STATUS_OS_CANCELADO_CONTRATO = STATUS_OS_CANCELADO_CONTRATO

  displayedColumns: string[] = [
    'tg',
    'data',
    'horario',
    'cliente',
    'consultora',
    'origem',
    'destino',
    'tipoServico',
    'veiculo',
    'motorista',
    'passageiro',
    'farol',
    'acoes',
  ]

  tableSource = new MatTableDataSource<LinhaMonitoramentoVm>()

  ngOnChanges(): void {
    this.tableSource.data = this.dataSource
  }

  onConfirmarFase(item: LinhaMonitoramentoVm, fase: FaseFarolVm): void {
    this.confirmarFase.emit({ item, fase })
  }

  // Cor da linha = estado agregado do farol (já é "a fase não confirmada mais
  // antiga" por definição, ver corDaFase/StatusServico no model). No prazo e
  // finalizado ficam no cinza padrão da tabela — só alerta/crítico pintam a linha.
  // semAlocacao não é uma cor do farol (motorista/veículo faltando é dimensão à
  // parte, ver doc sprint 9/10 seção 06) mas ainda pinta a linha de amarelo — só
  // não sobrepõe o crítico, que é mais urgente.
  rowClass(row: LinhaMonitoramentoVm): string {
    if (row.statusId === STATUS_OS_CANCELADO || row.statusId === STATUS_OS_CANCELADO_CONTRATO) {
      return 'style-row--cancelado'
    }
    const status = row.farol.statusServico
    if (status === 'critico') return 'style-row--critico'
    if (status === 'alerta' || row.semAlocacao) return 'style-row--alerta'
    return ''
  }

  // Não conectar dataSource.sort — a ordenação é do back (search-paginated), o
  // matSort aqui só dá a UI e emite pra página refazer a busca com ordenarPor/direcao.
  onSort(event: Sort): void {
    this.sortChange.emit(event.direction ? { ordenarPor: event.active, direcao: event.direction } : null)
  }

  // Sprint 9/10, seção 07 — pinta de amarelo só a célula/valor que corresponde ao
  // campo alterado, não a linha inteira. campos em camelCase (forma canônica);
  // o campo cru de cada item de alteracoes passa por normalizarCampoAlteracao
  // antes de comparar, então funciona com snake_case ou camelCase vindo do back.
  private ultimaAlteracao(element: LinhaMonitoramentoVm, ...campos: string[]): AlteracaoVm | null {
    return element.alteracoes?.find((a) => campos.includes(normalizarCampoAlteracao(a.campo))) ?? null
  }

  campoAlterado(element: LinhaMonitoramentoVm, ...campos: string[]): boolean {
    return this.ultimaAlteracao(element, ...campos) != null
  }

  // Texto do tooltip da célula destacada — vazio quando não há alteração (o
  // matTooltipDisabled do template cuida de não exibir nada nesse caso).
  tooltipAlteracao(element: LinhaMonitoramentoVm, ...campos: string[]): string {
    const alt = this.ultimaAlteracao(element, ...campos)
    if (!alt) return ''
    return `Alterado de "${alt.de ?? '-'}" para "${alt.para ?? '-'}" por ${alt.por}`
  }
}
